import React from "react";
import _ from "lodash";
import { NetworkAPI } from "apis";

import ModalWrapper from "components/common/ModalWrapper";
import LoadingUI from "components/common/LoadingUI";
import NetworkCustomFirstLookForm from "./NetworkCustomFirstLookForm";
import PreviewInJsonPopover from "../PreviewInJsonPopover";

import { MESSAGE } from "constants/Message";
import { isSectionEmpty } from "helpers/Utils";
import { cleanupRule } from "helpers/CustomUprFirstLookUtils";
import { MODAL_TYPE } from "./Constants";

class CustomFirstLookSettingModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isEditing: false,
      msg: [],

      rules: [],
      importRules: [],
      autoImport: null,

      currentRule: props.currentRule,
      currentIdx: [MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(props.modalType)
        ? -1
        : props.currentIdx,
      currentId: null,
    };

    this.formRef = React.createRef();

    this.handleSaveRule = this.handleSaveRule.bind(this);
  }

  async componentDidMount() {
    const { networkId, modalType, importIdList } = this.props;
    if ([MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType)) {
      const { rules } = await NetworkAPI.getFirstLookRules({
        networkId,
        isForceRefresh: false,
      });

      const parsedRule = _.map(rules, (rule) => this.transformRule(rule));

      const importRules = _.filter(parsedRule, (item) =>
        importIdList.includes(item.externalId)
      );

      const { cstConfig } = await NetworkAPI.getCstConfig({
        networkId,
      });

      let autoImport = null;
      if (
        modalType === MODAL_TYPE.ADD &&
        cstConfig &&
        cstConfig.general &&
        cstConfig.general.key &&
        cstConfig.general.managedValues
      ) {
        autoImport = {
          keyId: cstConfig.general.key.id.toString(),
          valueIds: cstConfig.general.managedValues.map((v) => v.id.toString()),
          operator: "IS",
        };
      }

      this.setState({
        rules: parsedRule,
        importRules,
        autoImport,
      });
    }
    this.setState({ isLoading: false });
  }

  handleSaveRule() {
    const {
      handleAddRule,
      handleValidate,
      handleEditRule,
      modalType,
      handleCloseModal,
    } = this.props;
    const { currentRule, currentId, currentIdx, msg } = this.state;

    if (_.isNil(handleValidate)) return;

    const validateResult = handleValidate(currentRule);

    if (
      validateResult.length === 0 ||
      (validateResult[1] === MESSAGE.WARNING && _.isEqual(validateResult, msg))
    ) {
      const cleanedRule = this._cleanupRule(currentRule);
      if ([MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType)) {
        handleAddRule({ rule: cleanedRule, id: currentId, idx: currentIdx });
        if (this.formRef && this.formRef.current) {
          const hasNext = this.formRef.current.handleSaveRule();
          if (!hasNext) handleCloseModal();
        }
      } else {
        handleEditRule({ rule: cleanedRule, idx: currentIdx });
      }
    } else {
      this.setState({ msg: validateResult });
    }
  }

  transformRule(rule) {
    const { targetingSection } = rule;

    const result = {};

    if (targetingSection.inventory) {
      if (!isSectionEmpty(targetingSection.inventory.unitIds)) {
        if (!isSectionEmpty(targetingSection.inventory.unitIds.includes)) {
          _.set(
            result,
            ["targetingSection", "inventory", "unitIds"],
            targetingSection.inventory.unitIds.includes
          );
        }
        if (!isSectionEmpty(targetingSection.inventory.unitIds.excludes)) {
          _.set(
            result,
            ["targetingSection", "inventory", "excludedUnitIds"],
            targetingSection.inventory.unitIds.excludes
          );
        }
      }

      if (!isSectionEmpty(targetingSection.inventory.placementIds)) {
        _.set(
          result,
          ["targetingSection", "inventory", "placementIds"],
          targetingSection.inventory.placementIds
        );
      }
    }

    if (!isSectionEmpty(targetingSection.os)) {
      _.set(result, ["targetingSection", "os"], targetingSection.os);
    }

    if (!isSectionEmpty(targetingSection.deviceCategory)) {
      _.set(
        result,
        ["targetingSection", "deviceCategory"],
        targetingSection.deviceCategory
      );
    }

    if (!isSectionEmpty(targetingSection.country)) {
      _.set(result, ["targetingSection", "country"], targetingSection.country);
    }

    if (!isSectionEmpty(targetingSection.mobileApplication)) {
      _.set(
        result,
        ["targetingSection", "mobileApplication"],
        targetingSection.mobileApplication
      );
    }

    if (!isSectionEmpty(targetingSection.inventoryUrl)) {
      _.set(
        result,
        ["targetingSection", "inventoryUrl"],
        targetingSection.inventoryUrl
      );
    }

    if (!isSectionEmpty(targetingSection.inventorySize)) {
      _.set(
        result,
        ["targetingSection", "inventorySize"],
        targetingSection.inventorySize
      );
    }

    if (!isSectionEmpty(targetingSection.customs)) {
      _.set(result, ["targetingSection", "customs"], targetingSection.customs);
    }

    let priceSettings = [];
    if (rule.priceSection && rule.priceSection.priceSettings) {
      priceSettings = _.map(rule.priceSection.priceSettings, (item) => {
        return _.isEmpty(item.targeting) ? _.omit(item, "targeting") : item;
      });
    }

    return {
      ...result,
      externalId: rule.externalId,
      name: rule.name,
      type: rule.type,
      priceSection: {
        priceSettings,
      },
    };
  }

  // cleanup rules when saving or previewing
  // remove empty arrays/objects in targeting sections
  _cleanupRule(rule) {
    return cleanupRule(rule);
  }

  render() {
    const {
      isLoading,
      isEditing,
      rules,
      importRules,
      autoImport,
      currentRule,
      currentIdx,
      msg,
    } = this.state;

    const {
      networkId,
      networkInfo,
      currency,
      customRules,
      placements,

      isModalOpen,
      modalType,
      importIdList,

      isSaving,

      handleCloseModal,
    } = this.props;

    const enabledRules = _.filter(
      customRules[currentRule.type] || [],
      (item) => item.enabled || _.isNil(item.enabled)
    );

    return (
      <ModalWrapper
        isOpen={isModalOpen}
        showCloseFooter={false}
        handleClose={handleCloseModal}
        customOverlayStyle={{ zIndex: 100 }}
        width="80%"
        height="90%"
      >
        <div className="flex h-full flex-col">
          <div className="mb-2 text-xl font-bold text-gray-700">
            {[MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType)
              ? "Add"
              : "Edit"}{" "}
            Rule
          </div>
          {isLoading && <LoadingUI iconOnly={true}></LoadingUI>}
          {!isLoading && (
            <>
              <div
                className="border overflow-y-auto"
                // style={{ maxHeight: "620px" }}
              >
                <NetworkCustomFirstLookForm
                  ref={this.formRef}
                  importRules={importRules}
                  importIdList={importIdList}
                  autoImport={autoImport}
                  networkId={networkId}
                  networkInfo={networkInfo}
                  placements={placements}
                  rules={rules}
                  currency={currency}
                  ruleCount={enabledRules.length}
                  currentIdx={currentIdx}
                  ruleSetting={_.cloneDeep(currentRule)}
                  modalType={modalType} // "ADD", "EDIT"
                  handleLoading={(isLoading) => this.setState({ isLoading })}
                  handleIsEditing={(val) => this.setState({ isEditing: val })}
                  handleCurrentRuleChanged={(rule) => {
                    this.setState({
                      currentRule: rule,
                    });
                  }}
                  handleImportIdChanged={(id) => {
                    this.setState({
                      currentId: id,
                    });
                  }}
                  handlePriorityChange={(val) =>
                    this.setState({
                      currentIdx: val,
                    })
                  }
                ></NetworkCustomFirstLookForm>
              </div>
              <div className="flex w-full pt-4">
                <div className="w-2/5">
                  {autoImport && (
                    <div className="rounded ml-8 bg-orange-200 px-2 py-1 text-xs text-orange-700">
                      <div className="font-semibold">Autofilled targeting</div>
                      <div>1. Virtual placements: Running units</div>
                      <div>2. Customs: Intowow billing key</div>
                    </div>
                  )}
                </div>
                <div className="flex w-1/5 items-center justify-center">
                  <PreviewInJsonPopover
                    place={"top"}
                    json={JSON.stringify(
                      this._cleanupRule(currentRule),
                      null,
                      4
                    )}
                    triggerElement={
                      <button type="button" className="px-4 py-2 text-blue-700">
                        Preview in JSON
                      </button>
                    }
                  ></PreviewInJsonPopover>
                </div>
                <div className="flex w-2/5 items-center justify-end">
                  {msg.length > 0 && (
                    <div
                      className={`${
                        msg[1] === MESSAGE.ERROR ? "text-red-600" : ""
                      } ${msg[1] === MESSAGE.WARNING ? "text-yellow-600" : ""}`}
                    >
                      {msg[0]}
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={isSaving}
                    className={`px-4 py-2 text-blue-700 ${
                      isSaving ? "cursor-not-allowed" : ""
                    }`}
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 ${
                      isEditing || isSaving
                        ? "cursor-not-allowed bg-gray-400 text-gray-800"
                        : "bg-blue-500 text-white hover:bg-blue-700"
                    }  rounded shadow font-semibold`}
                    onClick={this.handleSaveRule}
                    disabled={isEditing || isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : [MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType)
                      ? "Add"
                      : "Save edit"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

export default CustomFirstLookSettingModal;
